import { computed, action } from 'mobx';
import { IScatterChartProps } from '@kurtosys/ksys-app-components/dist/components/charting/ScatterChart/models';
import { StoreBase } from '../../../common/StoreBase';
import { IChartConfiguration } from '../models';
import { IDataRow } from '../../../models/IDataRow';
import { IChartData, IScatterProps } from '@kurtosys/ksys-app-components/dist/models/charting';
import { utils } from '@kurtosys/ksys-app-template';

export class ChartStore extends StoreBase {
	static componentKey: 'chart' = 'chart';

	@computed
	get configuration(): IChartConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(ChartStore.componentKey);
		}
	}

	@computed
	get useCustolTooltip(): boolean {
		return !!this.storeContext.customTooltipStore.configuration.useCustomTooltip;
	}

	@action
	async initialize(): Promise<void> {

	}

	@computed
	get data(): IDataRow[] {
		return this.storeContext.appStore.getData(this.configuration);
	}



	@computed
	get rawData(): IDataRow[] {
		return this.storeContext.appStore.rawData;
	}

	@computed
	get xAxisLabel(): string | undefined {
		if (this.configuration && this.configuration.chartProps && this.configuration.chartProps.elementOverrides && this.configuration.chartProps.elementOverrides.xAxis && this.configuration.chartProps.elementOverrides.xAxis.labelQuery) {
			const xAxisLabelQuery = this.configuration.chartProps.elementOverrides.xAxis.labelQuery;
			const label: string = this.getQueryValue(xAxisLabelQuery);
			return label;
		}
		return undefined;
	}

	@computed
	get yAxisLabel(): string | undefined {
		if (this.configuration && this.configuration.chartProps && this.configuration.chartProps.elementOverrides && this.configuration.chartProps.elementOverrides.yAxis && this.configuration.chartProps.elementOverrides.yAxis.labelQuery) {
			const yAxisLabelQuery = this.configuration.chartProps.elementOverrides.yAxis.labelQuery;
			const label: string = this.getQueryValue(yAxisLabelQuery);
			return label;
		}
		return undefined;
	}

	@computed
	get xAxisPivotReferenceLineLabel(): string | undefined {
		if (this.configuration && this.configuration.chartProps && this.configuration.chartProps.xPivotReferenceLine) {
			const xAxisLabelQuery = this.configuration.chartProps.xPivotReferenceLine.labelQuery;
			const label: string = this.getQueryValue(xAxisLabelQuery);
			return label;
		}
		return undefined;
	}

	@computed
	get yAxisPivotReferenceLineLabel(): string | undefined {
		if (this.configuration && this.configuration.chartProps && this.configuration.chartProps.yPivotReferenceLine) {
			const yAxisLabelQuery = this.configuration.chartProps.yPivotReferenceLine.labelQuery;
			const label: string = this.getQueryValue(yAxisLabelQuery);
			return label;
		}
		return undefined;
	}

	@computed
    get highlightValue(): string | undefined {
        if (
            this.configuration &&
            this.configuration.chartProps &&
            this.configuration.chartProps.highlightDataPointProps
        ) {
            const highlightValueQuery =
                this.configuration.chartProps.highlightDataPointProps
                    .valueQuery;
            const value: string = this.getQueryValue(highlightValueQuery);
            return value;
        }
        return undefined;
    }

	@computed
	get props(): IScatterChartProps {
		if (this.configuration) {
			const chartProps = this.configuration.chartProps;
			const data = this.data;
			const seriesProps: Partial<IScatterProps>[] = [];
			for (const row of data) {
				seriesProps.push({
					data: [row],
					name: row.name,
					fill: row.color,
					shape: row.shape || this.configuration.defaultShape || 'circle',
				});
			}
			if (chartProps) {
				if (!chartProps.chartBaseComponent) {
					chartProps.chartBaseComponent = 'ScatterChart';
				}
				if (chartProps.composedChartProps) {
					chartProps.composedChartProps.data = this.data as IChartData[];
				}
				if (chartProps.highLowBubbleChartProps && chartProps.composedChartProps) {
					chartProps.composedChartProps.data = this.rawData as IChartData[];
				}
				else {
					chartProps.seriesProps = seriesProps;
				}
				if (chartProps.elementOverrides) {
					if (chartProps.elementOverrides.yAxis && this.yAxisLabel) {
						if (utils.typeChecks.isObject(chartProps.elementOverrides.yAxis.label)) {
							chartProps.elementOverrides.yAxis.label = {
								...chartProps.elementOverrides.yAxis.label,
								value: this.yAxisLabel
							};
						}
						else {
							chartProps.elementOverrides.yAxis.label = this.yAxisLabel;
						}
					}
					if (chartProps.elementOverrides.xAxis && this.xAxisLabel) {
						if (utils.typeChecks.isObject(chartProps.elementOverrides.xAxis.label)) {
							chartProps.elementOverrides.xAxis.label = {
								...chartProps.elementOverrides.xAxis.label,
								value: this.xAxisLabel
							};
						}
						else {
							chartProps.elementOverrides.xAxis.label = this.xAxisLabel;
						}
					}
				}
				if (chartProps.xPivotReferenceLine && this.xAxisPivotReferenceLineLabel) {
					chartProps.xPivotReferenceLine.label = {
						...chartProps.xPivotReferenceLine.label,
						value: this.xAxisPivotReferenceLineLabel
					};
				}
				if (chartProps.yPivotReferenceLine && this.yAxisPivotReferenceLineLabel) {
					chartProps.yPivotReferenceLine.label = {
						...chartProps.yPivotReferenceLine.label,
						value: this.yAxisPivotReferenceLineLabel
					};
				}
				if (chartProps.highlightDataPointProps && this.highlightValue) {
                    chartProps.highlightDataPointProps.value =
                        this.highlightValue;
                }
				return chartProps;
			}
		}
		return {};
	}
}